import { usePathwrightContext } from "@pathwright/features/src/modules/pathwright/PathwrightContext"
import { getUrlFromRoute } from "@pathwright/ui/src/components/link/utils"
import { getRoute } from "@pathwright/features/src/modules/utils/urls"
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore
} from "react"
import { Redirect, Route, useHistory } from "react-router-dom"
import RouteCardStack from "../components/cards/RouteCardStack"
import Modal, { ChakraModal } from "../components/modal/Modal"
import { OverlayPortal } from "./OverlaySlots"
import { getLocationUrl, getRouteKind, toStoredLocation } from "./routeRegistry"
import { getLegacyRouterState } from "./utils"

// The same route table renders twice: the background layer matches the
// background location and renders pages; the foreground layer matches the
// current location and renders overlays (modals, the inbox, card stacks).
export const RouteLayerContext = React.createContext({
  layer: "foreground",
  location: null,
  backgroundLocation: null
})

export const useRouteLayer = () => useContext(RouteLayerContext)

export const RouteLayer = ({ layer, location, backgroundLocation, children }) => (
  <RouteLayerContext.Provider value={{ layer, location, backgroundLocation }}>
    {children}
  </RouteLayerContext.Provider>
)

const useRouteState = (route, { history, location, match }, extra) => {
  const pwContext = usePathwrightContext()

  return {
    ...route,
    history,
    location,
    match,
    ...getLegacyRouterState({ history, location, match }),
    // Some routes depend on legacy context, though unsure how they
    // were provided that data in the first place.
    school: window.school,
    user: window.user,
    // Adding newer context to routes:
    pwContext,
    ...extra
  }
}

const RouteOverlay = ({ routeState }) => {
  const { components = {}, cards } = routeState
  const {
    secondary: SecondaryComponent,
    modal: ModalComponent,
    chakraModal: ChakraModalComponent
  } = components

  return (
    <>
      {SecondaryComponent ? (
        <OverlayPortal slot="modal">
          <SecondaryComponent {...routeState} />
        </OverlayPortal>
      ) : null}
      {/* Prefer legacy modal over Chakra modal */}
      {ModalComponent ? (
        <OverlayPortal slot="modal">
          <Modal>
            <ModalComponent {...routeState} />
          </Modal>
        </OverlayPortal>
      ) : ChakraModalComponent ? (
        <OverlayPortal slot="modal">
          <ChakraModal
            fillViewport={!!routeState.isFullscreen}
            labelledBy={routeState.modalLabelledBy}
            onClose={
              routeState.onClose
                ? () => routeState.onClose(routeState)
                : undefined
            }
          >
            <ChakraModalComponent {...routeState} />
          </ChakraModal>
        </OverlayPortal>
      ) : null}
      {cards?.length ? (
        <OverlayPortal slot="cards">
          <Modal className="Modal--cardstack" key={cards[0].key}>
            <RouteCardStack {...routeState} />
          </Modal>
        </OverlayPortal>
      ) : null}
    </>
  )
}

// Nested routes render their page at different depths, so moving between a
// route and its child (path -> step) would remount a page they share. Instead,
// background routes publish their page and BackgroundPage renders it in one
// place, keeping it mounted while its component stays the same.
export const BackgroundPageContext = React.createContext(null)

export function createBackgroundPageStore() {
  let page = null
  const listeners = new Set()
  return {
    get: () => page,
    set(next) {
      page = next
      listeners.forEach((listener) => listener())
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    }
  }
}

// Until another route publishes, the last page stays up (e.g. while the next
// route's async onEnter or lazy routes load).
const usePublishBackgroundPage = (page) => {
  const store = useContext(BackgroundPageContext)
  useLayoutEffect(() => {
    if (store && page) store.set(page)
  })
}

export const BackgroundPage = () => {
  const store = useContext(BackgroundPageContext)
  const page = useSyncExternalStore(store.subscribe, store.get)
  if (!page?.Component) return null

  const { Component, routeState } = page
  return (
    <Route
      path={routeState.match.path}
      location={routeState.location}
      exact
      strict
      render={() => <Component {...routeState} />}
    />
  )
}

// An overlay's own page, used when the background location is an overlay
// (e.g. its default background is another overlay).
const RouteFallback = ({ Component, route, history, location, match }) => {
  const routeState = useRouteState(route, { history, location, match })
  usePublishBackgroundPage({ Component, routeState })
  return null
}

const ActiveRoute = ({ route, component, history, location, match }) => {
  const {
    layer,
    location: currentLocation,
    backgroundLocation
  } = useRouteLayer()
  const routeHistory = useHistory()
  const routeState = useRouteState(
    route,
    { history, location, match },
    layer === "foreground" ? { modalLaunchedFrom: backgroundLocation } : null
  )
  const Component = route.component || component

  // Assuming we're awaiting a callback from route.onEnter if the function expects a third arg.
  // Note: function length property is unreliable: https://stackoverflow.com/a/4138036/6362803
  const isAsync = !!route.onEnter && route.onEnter.length === 3
  const [readyRoute, setReadyRoute] = useState(null)
  const awaitingCallback = isAsync && readyRoute !== route
  // While awaiting the next route of a card stack, keep showing the last one.
  const lastReadyRef = useRef(null)
  const [redirect, setRedirect] = useState(null)

  // A background behind an overlay isn't the current URL, so its redirects
  // change the background rather than navigating away from the overlay.
  const isBehindOverlay =
    layer === "background" &&
    !!currentLocation &&
    getLocationUrl(currentLocation) !== getLocationUrl(location)

  function handleReplace(redirect) {
    // Transform the redirect into a url (pathname + search)
    // since redirect may be a string or obj.
    const redirectUrl = getUrlFromRoute(redirect)

    let redirectUrlOrRoute = null

    // Preserve query params when replacing with same base route.
    if (
      location.pathname.startsWith(redirectUrl) ||
      redirectUrl.startsWith(location.pathname)
    ) {
      redirectUrlOrRoute = getRoute(redirectUrl, "", location.search)
    } else {
      redirectUrlOrRoute = redirectUrl
    }

    // Return to an obj so we can merge in state info.
    const redirectRoute = getRoute(redirectUrlOrRoute)

    // Merge in the redirectedFrom state. Routes can use this to determine if
    // they have been redirected to.
    const redirectRouteWithState = {
      ...redirectRoute,
      state: {
        ...redirectRoute.state,
        // Preserve the from location for potential use in the redirected route.
        redirectedFrom: location
      }
    }

    if (isBehindOverlay) {
      routeHistory.replace({
        ...currentLocation,
        state: {
          ...currentLocation.state,
          background: toStoredLocation(redirectRouteWithState)
        }
      })
    } else {
      setRedirect(redirectRouteWithState)
    }
  }

  // Handle legacy middleware methods (onEnter).
  useEffect(() => {
    // Calling route.onEnter every time the location changes.
    if (route.onEnter) {
      // HACK ALERT: Callback in legacy onEnter middleware would allow for only rendering
      // next component when route was ready (in cases where there were some async operations).
      const callback = async (maybeFn) => {
        // Potentially await the result of the callback arg.
        let maybePromise = null
        if (typeof maybeFn === "function") maybePromise = maybeFn()
        if (maybePromise instanceof Promise) await maybePromise
        setReadyRoute(route)
      }
      try {
        route.onEnter(routeState, handleReplace, callback)
      } catch (error) {
        console.log("Routing error in onEnter: ", error)
      }
    }
  }, [location, route])

  // Handle legacy middleware methods (onLeave).
  useEffect(() => {
    if (route.onLeave) {
      return () => route.onLeave(routeState)
    }
  }, [route])

  // Clear redirect if location matches redirect. This indicates
  // that the current location has successfully been redirected to.
  useEffect(() => {
    const url = getUrlFromRoute(location)
    const redirectUrl = getUrlFromRoute(redirect)

    if (url === redirectUrl) {
      setRedirect(null)
    }
  }, [location])

  usePublishBackgroundPage(
    layer === "background" && match.isExact && !redirect && !awaitingCallback
      ? { Component, routeState }
      : null
  )

  // Allowing Router to redirect
  if (redirect) {
    return <Redirect to={redirect} />
  }

  // The route must match exactly to render; parent routes only render their children.
  if (!match.isExact || layer !== "foreground") return null

  if (!awaitingCallback) lastReadyRef.current = { routeState, Component }
  const ready = awaitingCallback ? lastReadyRef.current : { routeState, Component }
  if (!ready) return null

  return <RouteOverlay routeState={ready.routeState} />
}

const AppRoute = (props) => {
  const { route, component, match } = props
  const { layer } = useRouteLayer()
  const kind = getRouteKind(route)

  if (layer === "foreground" && kind === "page") return null

  if (layer === "background" && kind === "overlay") {
    const Component = route.component || component
    return Component && match.isExact ? (
      <RouteFallback {...props} Component={Component} />
    ) : null
  }

  return <ActiveRoute {...props} />
}

export default AppRoute
